import type { ClientChatMessage } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Menu, Text } from 'react-native-paper';

import { ContentCopyIcon, DeleteIcon } from '@/assets';
import { useDeleteChatMessage } from '@/features/api/hooks/communication-chat-events/useDeleteChatMessage';
import { useAppState } from '@/features/authorization/provider/useAppState';
import { ChatMessageImages } from '@/features/chats/components/ChatMessageImages';
import { copyTextToClipboard } from '@/features/chats/utils/message';
import { usePopup } from '@/features/popup/provider/usePopup';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { ExtendedMenu } from '@/shared/components/ExtendedMenu';
import { AutoLinkText } from '@/shared/components/text/AutoLinkText';
import { hasItems } from '@/shared/utils/array';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import { dayjs } from '@/time';

const makeStyles = (textColor: string) =>
  StyleSheet.create({
    container: { flexDirection: 'column', alignItems: 'stretch' },
    text: { color: textColor },
    deleted: { color: textColor, fontStyle: 'italic' },
    time: { color: textColor, textAlign: 'right' }
  });

type Props = {
  message: ClientChatMessage;
  textColor: string;
};

export const ChatMessageContent: FC<Props> = ({ message, textColor }) => {
  const time = useMemo(
    () => dayjs(message.sentTime).format('LT'),
    [message.sentTime]
  );
  const styles = useMemo(() => makeStyles(textColor), [textColor]);
  const theme = useAppTheme();
  const { showAlert } = usePopup();

  const deleteChatMessage = useDeleteChatMessage();

  const images = message.images ?? [];
  const { t } = useTranslation(LocalizationNameSpace.CHATS, {
    keyPrefix: 'components.messageMenu'
  });

  const { userId: ownUserId } = useAppState();
  const [menuVisible, setMenuVisible] = useState(false);

  return (
    <View style={styles.container}>
      {!message.deleted && hasItems(images) && (
        <ChatMessageImages mediaItems={images} />
      )}
      <TouchableOpacity
        accessibilityRole="button"
        activeOpacity={0.6}
        disabled={message.deleted}
        onLongPress={() => setMenuVisible(true)}
      >
        <ExtendedMenu
          visible={menuVisible}
          onDismiss={() => setMenuVisible(false)}
          anchor={
            <AutoLinkText
              variant="bodyLarge"
              style={message.deleted ? styles.deleted : styles.text}
              linkStyle={styles.text}
              text={message.deleted ? t('deleted') : message.message}
            />
          }
        >
          <Menu.Item
            leadingIcon={({ color, size }) => (
              <ContentCopyIcon fill={color} width={size} height={size} />
            )}
            onPress={async () => {
              await copyTextToClipboard(message);
              setMenuVisible(false);
            }}
            title={t('copy')}
          />
          {ownUserId === message.senderId && (
            <Menu.Item
              theme={{ colors: { onSurface: theme.colors.error } }}
              leadingIcon={({ size }) => (
                <DeleteIcon
                  fill={theme.colors.error}
                  width={size}
                  height={size}
                />
              )}
              onPress={() => {
                setMenuVisible(false);
                showAlert({
                  labels: t('deleteAlert', { returnObjects: true }),
                  confirmColor: theme.colors.error,
                  onConfirm: () => {
                    deleteChatMessage.mutate(message.id);
                  }
                });
              }}
              title={t('delete')}
            />
          )}
          {
            // TODO: Enable when backend supports editing of chat messages
            /* 
            <Menu.Item
              leadingIcon={(props) => (
                <EditIcon
                  fill={props.color}
                  width={props.size}
                  height={props.size}
                />
              )}
              onPress={() => {
                setMenuVisible(false);
              }}
              title={t('edit')}
            />
          */
          }
        </ExtendedMenu>
      </TouchableOpacity>
      <Text variant="bodySmall" style={styles.time}>
        {time}
      </Text>
    </View>
  );
};
